import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 8;
const PAD = 8;
const DRAWING_SIDEBAR_WIDTH = 48;
const CHART_TIP_ROOT = '.Chart';
const DRAWING_TOOLBAR = '.am5stock-control-drawing-tools';
const DRAWING_SIDEBAR =
  '.am5stock[data-am5-title="Drawing tool"] .am5stock-control-list-container, .am5stock[title="Drawing tool"] .am5stock-control-list-container';
const DRAWING_PANEL =
  `${DRAWING_TOOLBAR}, .am5stock[data-am5-title="Drawing tool"], .am5stock[title="Drawing tool"]`;
const DRAWING_TOOLBAR_TARGET =
  '.am5stock-control-drawing-tools > .am5stock, .am5stock-control-drawing-tools .am5stock-control-button';
const DRAWING_SIDEBAR_TARGET =
  '.am5stock[data-am5-title="Drawing tool"] .am5stock-control-list li, .am5stock[data-am5-title="Drawing tool"] .am5stock-list-item, .am5stock[title="Drawing tool"] .am5stock-control-list li, .am5stock[title="Drawing tool"] .am5stock-list-item';

function isDrawingSidebar(target) {
  return !!target?.closest?.(DRAWING_SIDEBAR);
}

function isDrawingToolbar(target) {
  return !!target?.closest?.(DRAWING_TOOLBAR) && !isDrawingSidebar(target);
}

function isDrawingPanel(target) {
  return isDrawingSidebar(target) || !!target?.closest?.(DRAWING_PANEL);
}

function findDrawingSidebarTarget(target) {
  return target?.closest?.(DRAWING_SIDEBAR_TARGET) || null;
}

function findDrawingToolbarTarget(target) {
  return target?.closest?.(DRAWING_TOOLBAR_TARGET) || null;
}

function isTooltipExcluded(target) {
  if (isDrawingPanel(target)) return false;
  if (target.closest('[class*="__menu"], [class*="__menu-portal"], [role="listbox"]')) {
    return true;
  }
  return !!target.closest(
    '.am5stock-control-list-container, .am5stock-control-list, .am5stock-list-item, .am5stock-list-search',
  );
}

function decodeTooltipText(text) {
  if (!text || !text.includes('&')) return text;
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

function tooltipTextFrom(el) {
  if (!el) return '';
  const raw = (
    el.getAttribute('data-tooltip')
    || el.getAttribute('data-am5-title')
    || el.getAttribute('title')
    || ''
  ).trim();
  return decodeTooltipText(raw);
}

function isVisibleEl(el) {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
    return false;
  }
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function isDrawingToolControl(el) {
  return !!el?.closest?.('.am5stock[data-am5-title="Drawing tool"], .am5stock[title="Drawing tool"]');
}

function findDrawingToolbarDropdownOwner() {
  const tools = document.querySelector(DRAWING_TOOLBAR);
  if (!tools || getComputedStyle(tools).display === 'none') return null;

  for (const stock of tools.querySelectorAll('.am5stock')) {
    const list = stock.querySelector('.am5stock-control-list-container');
    if (isVisibleEl(list)) return stock;
  }

  for (const btn of tools.querySelectorAll('.am5stock-control-button')) {
    const list = btn.querySelector('.am5stock-control-list-container');
    if (isVisibleEl(list)) return btn;
  }

  return null;
}

function isInsideDrawingToolbarDropdown(target) {
  return !!target?.closest?.(
    `${DRAWING_TOOLBAR} .am5stock-control-list-container, ${DRAWING_TOOLBAR} .am5stock-control-list, ${DRAWING_TOOLBAR} .am5stock-list-item`,
  );
}

function isChartToolbarTipSuppressed(target) {
  if (target.closest('.am5stock-control-list-container, .am5stock-control-list')) return false;

  const control = target.closest('#chartcontrols .am5stock');
  if (!control || isDrawingToolControl(control)) return false;

  const list = control.querySelector('.am5stock-control-list-container');
  return isVisibleEl(list);
}

function isDrawingSidebarTipSuppressed(sidebarEl) {
  return !!sidebarEl?.classList.contains('draw-sidebar-tool-active');
}

function isDrawingSidebarInteractiveTarget(target) {
  return !!(
    findDrawingSidebarTarget(target)
    || target?.closest?.('.draw-panel-close')
  );
}

function isDrawingToolbarTipSuppressed(toolbarEl) {
  if (!toolbarEl) return false;

  if (toolbarEl.classList?.contains('am5stock-control-button-active')) return true;

  const owner = findDrawingToolbarDropdownOwner();
  if (!owner) return false;

  return owner === toolbarEl || owner.contains(toolbarEl) || toolbarEl.contains(owner);
}

function isNearDrawingSidebar(anchor) {
  return anchor.left < DRAWING_SIDEBAR_WIDTH + anchor.width;
}

const TOOLBAR_TIP_GAP = 5;

function hoverBorderBottom(el) {
  const svg = el.querySelector?.('.am5stock-control-icon svg, svg');
  const box = svg?.getBoundingClientRect?.();
  if (box && box.height > 0) return box.bottom;
  return el.getBoundingClientRect().bottom;
}

function placeTip(el, text, pointerX, pointerY) {
  const anchor = el.getBoundingClientRect();
  const below = anchor.top < 56;
  const inDrawingTools = !!el.closest?.(DRAWING_TOOLBAR);
  const clearSubtoolbar = !!el.closest?.('#chartcontrols') && !inDrawingTools;
  const hoverBottom = (clearSubtoolbar || inDrawingTools) ? hoverBorderBottom(el) : anchor.bottom;
  const belowGap = (clearSubtoolbar || inDrawingTools) ? TOOLBAR_TIP_GAP : GAP;
  return {
    x: anchor.left + anchor.width / 2,
    y: below ? hoverBottom + belowGap : anchor.top - GAP,
    below,
    placement: 'top',
    text,
    anchor,
    hoverBottom,
    belowGap,
    pointerX: pointerX ?? anchor.left + anchor.width / 2,
    pointerY: pointerY ?? anchor.top + anchor.height / 2,
  };
}

function placeTipRight(el, text, pointerX, pointerY) {
  const anchor = el.getBoundingClientRect();
  return {
    x: anchor.right + GAP,
    y: anchor.top + anchor.height / 2,
    below: false,
    placement: 'right',
    text,
    anchor,
    pointerX: pointerX ?? anchor.left + anchor.width / 2,
    pointerY: pointerY ?? anchor.top + anchor.height / 2,
  };
}

function clampNock(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

function nockOffsets(tip, rect, placement) {
  const px = tip.pointerX ?? tip.anchor.left + tip.anchor.width / 2;
  const py = tip.pointerY ?? tip.anchor.top + tip.anchor.height / 2;
  const edge = 10;

  if (placement === 'right' || placement === 'left') {
    return {
      nockX: null,
      nockY: clampNock(py - rect.top, edge, rect.height - edge),
    };
  }

  return {
    nockX: clampNock(px - rect.left, edge, rect.width - edge),
    nockY: null,
  };
}

function migrateTitle(el) {
  if (!el?.getAttribute || el.nodeType !== 1) return;
  if (isTooltipExcluded(el)) {
    el.removeAttribute('title');
    el.removeAttribute('data-tooltip');
    return;
  }
  const text = decodeTooltipText(el.getAttribute('title')?.trim());
  if (!text) {
    if (el.hasAttribute('title')) {
      el.removeAttribute('data-tooltip');
      el.removeAttribute('data-am5-title');
    }
    return;
  }
  el.setAttribute('data-tooltip', text);
  if (el.closest?.(CHART_TIP_ROOT)) {
    el.setAttribute('data-am5-title', text);
  }
  el.removeAttribute('title');
}

function migrateTitles(root = document.body) {
  root.querySelectorAll?.('[title]').forEach(migrateTitle);
  root.querySelectorAll?.('[data-tooltip]').forEach((el) => {
    const decoded = decodeTooltipText(el.getAttribute('data-tooltip')?.trim());
    if (decoded) el.setAttribute('data-tooltip', decoded);
  });
}

function isTruncated(el) {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
}

function cellText(el) {
  return (el?.textContent || '').replace(/\s+/g, ' ').trim();
}

function clampTipPosition(tip, node) {
  node.classList.remove('is-below', 'is-right', 'is-left');

  if (tip.placement === 'right') {
    const anchor = tip.anchor;
    let x = anchor.right + GAP;
    let y = anchor.top + anchor.height / 2;
    let placement = 'right';

    node.classList.add('is-right');
    node.style.left = `${x}px`;
    node.style.top = `${y}px`;

    let rect = node.getBoundingClientRect();
    let halfH = rect.height / 2;
    y = Math.max(PAD + halfH, Math.min(y, window.innerHeight - PAD - halfH));

    if (rect.right > window.innerWidth - PAD) {
      placement = 'left';
      x = anchor.left - GAP;
      node.classList.remove('is-right');
      node.classList.add('is-left');
      node.style.left = `${x}px`;
      rect = node.getBoundingClientRect();
      x = anchor.left - GAP - rect.width;
      node.style.left = `${x}px`;
    }

    node.style.top = `${y}px`;
    rect = node.getBoundingClientRect();
    const nock = nockOffsets(tip, rect, placement);
    return { x, y, below: false, placement, ...nock };
  }

  const anchor = tip.anchor;
  let nextBelow = tip.below;
  const belowAnchor = tip.hoverBottom ?? anchor.bottom;
  const belowGap = tip.belowGap ?? GAP;
  let y = nextBelow ? belowAnchor + belowGap : anchor.top - GAP;

  node.classList.toggle('is-below', nextBelow);
  node.style.left = `${tip.x}px`;
  node.style.top = `${y}px`;

  let rect = node.getBoundingClientRect();
  if (!nextBelow && rect.top < PAD) {
    nextBelow = true;
    y = belowAnchor + belowGap;
  } else if (nextBelow && rect.bottom > window.innerHeight - PAD) {
    nextBelow = false;
    y = anchor.top - GAP;
  }

  node.classList.toggle('is-below', nextBelow);
  node.style.top = `${y}px`;
  rect = node.getBoundingClientRect();

  const halfW = rect.width / 2;
  let x = Math.max(PAD + halfW, Math.min(tip.x, window.innerWidth - PAD - halfW));
  if (isNearDrawingSidebar(anchor)) {
    x = Math.max(x, DRAWING_SIDEBAR_WIDTH + halfW + GAP);
  }

  node.style.left = `${x}px`;

  if (nextBelow && rect.bottom > window.innerHeight - PAD) {
    y = Math.max(PAD, window.innerHeight - PAD - rect.height);
  } else if (!nextBelow && rect.top < PAD) {
    y = PAD + rect.height;
  }

  node.style.top = `${y}px`;
  rect = node.getBoundingClientRect();
  const placement = nextBelow ? 'bottom' : 'top';
  const nock = nockOffsets(tip, rect, placement);
  return { x, y, below: nextBelow, placement, ...nock };
}

/** Global themed tooltip: data-tooltip, migrated titles, truncated table cells. */
export default function TableCellTooltip() {
  const [tip, setTip] = useState(null);
  const [pos, setPos] = useState(null);
  const tipRef = useRef(null);

  useLayoutEffect(() => {
    if (!tip || !tipRef.current) {
      setPos(null);
      return;
    }
    setPos(clampTipPosition(tip, tipRef.current));
  }, [tip]);

  useEffect(() => {
    let raf = 0;
    let hoveredTarget = null;

    const hide = () => {
      cancelAnimationFrame(raf);
      setTip(null);
      setPos(null);
    };

    const showFor = (target, pointerX, pointerY) => {
      hoveredTarget = target;
      if (!target?.closest) {
        hide();
        return;
      }

      if (isTooltipExcluded(target)) {
        hide();
        return;
      }

      if (isDrawingSidebar(target)) {
        if (!isDrawingSidebarInteractiveTarget(target)) {
          hide();
          return;
        }

        const sidebarEl = findDrawingSidebarTarget(target)
          || target.closest('.draw-panel-close');
        if (isDrawingSidebarTipSuppressed(sidebarEl)) {
          hide();
          return;
        }
        const text = tooltipTextFrom(sidebarEl);
        if (text && sidebarEl && isVisibleEl(sidebarEl)) {
          setPos(null);
          setTip(placeTipRight(sidebarEl, text, pointerX, pointerY));
          return;
        }
        hide();
        return;
      }

      if (isDrawingToolbar(target)) {
        if (isInsideDrawingToolbarDropdown(target)) {
          hide();
          return;
        }

        const toolbarEl = findDrawingToolbarTarget(target)
          || target.closest('.draw-subtoolbar-toggle[data-tooltip], [data-tooltip]');
        if (isDrawingToolbarTipSuppressed(toolbarEl)) {
          hide();
          return;
        }
        const text = tooltipTextFrom(toolbarEl);
        if (text && toolbarEl && isVisibleEl(toolbarEl)) {
          setPos(null);
          const anchor = toolbarEl.getBoundingClientRect();
          if (isNearDrawingSidebar(anchor)) {
            setTip(placeTipRight(toolbarEl, text, pointerX, pointerY));
          } else {
            setTip(placeTip(toolbarEl, text, pointerX, pointerY));
          }
          return;
        }
        hide();
        return;
      }

      if (isDrawingPanel(target)) {
        hide();
        return;
      }

      if (isChartToolbarTipSuppressed(target)) {
        hide();
        return;
      }

      const labeled = target.closest('[data-tooltip]');
      if (labeled) {
        const text = tooltipTextFrom(labeled);
        if (text) {
          setPos(null);
          setTip(labeled.matches('.star-td')
            ? placeTipRight(labeled, text)
            : placeTip(labeled, text, pointerX, pointerY));
          return;
        }
      }

      const ellipsis = target.closest('.app-cell-ellipsis, .qty-ellipsis, .jPayload, .symbol-name-text, .symbol-row-description');
      if (ellipsis && isTruncated(ellipsis)) {
        const text = cellText(ellipsis);
        if (text && text !== '---' && text !== '--' && text !== '—') {
          setPos(null);
          setTip(placeTip(ellipsis, text, pointerX, pointerY));
          return;
        }
      }

      const cell = target.closest('.rdt_TableCell');
      if (!cell || cell.closest('.rdt_TableHead')) {
        hide();
        return;
      }
      if (target.closest('.modal-icon, .positions-button, .close-icon, .Close-button')) {
        hide();
        return;
      }

      const inner = cell.querySelector(':scope > div') || cell;
      const el = [cell.querySelector('.app-cell-ellipsis'), inner, cell].find(isTruncated);
      const text = cellText(el);
      if (!el || !text || text === '---' || text === '--' || text === '—') {
        hide();
        return;
      }
      setPos(null);
      setTip(placeTip(el, text, pointerX, pointerY));
    };

    const onOver = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => showFor(e.target, e.clientX, e.clientY));
    };

    const onOut = event => {
      const source = event.target?.closest?.('[data-tooltip], .symbol-name-text');
      if (source && !source.contains(event.relatedTarget)) hide();
    };
    document.addEventListener('mouseout', onOut);
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mousedown', hide, true);
    document.addEventListener('click', hide, true);
    document.addEventListener('mouseleave', hide);
    document.addEventListener('scroll', hide, true);

    migrateTitles(document.body);
    const titleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && hoveredTarget &&
            (mutation.target === hoveredTarget || mutation.target.contains(hoveredTarget))) {
          hide();
        }
        if (mutation.type === 'attributes' && mutation.attributeName === 'title') {
          migrateTitle(mutation.target);
          return;
        }
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          migrateTitle(node);
          migrateTitles(node);
        });
      });
    });
    titleObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['title', 'data-tooltip', 'disabled'],
    });

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mousedown', hide, true);
      document.removeEventListener('click', hide, true);
      document.removeEventListener('mouseleave', hide);
      document.removeEventListener('scroll', hide, true);
      titleObserver.disconnect();
    };
  }, []);

  if (!tip) return null;

  const renderPos = pos || tip;
  const tipClass = renderPos.placement === 'right'
    ? ' is-right'
    : renderPos.placement === 'left'
      ? ' is-left'
      : renderPos.below
        ? ' is-below'
        : '';

  return createPortal(
    <div
      ref={tipRef}
      className={`app-cell-tooltip${tipClass}`}
      style={{
        left: renderPos.x,
        top: renderPos.y,
        visibility: pos ? 'visible' : 'hidden',
        ...(renderPos.nockX != null ? { '--tip-nock-x': `${renderPos.nockX}px` } : {}),
        ...(renderPos.nockY != null ? { '--tip-nock-y': `${renderPos.nockY}px` } : {}),
      }}
    >
      {tip.text}
    </div>,
    document.body
  );
}
